import type { CurrencyCode } from '@noths/polaris-client-localisation';
import { getDeliveryByEventName, getProductImageURL } from '@noths/polaris-client-utils';
import type { ProductCardProps } from '@noths/polaris-design-system-candidate-components';
import { toProductCardBadge } from '@noths/polaris-design-system-candidate-components';
import { addDays } from 'date-fns';

import { matchPromoLabel } from 'src/redux/content/matchPromoLabel';
import type { PromoLabel } from 'src/redux/content/types';
import type { Product } from 'src/redux/products/types/Product';
import type { Price } from 'src/types/price';

/**
 * The card props that come from the product itself. The handlers, the favourite
 * state and `priority` are supplied by the call site, because those are about the
 * card's place on the page rather than about the product.
 */
export type ProductCardDataProps = Pick<
  ProductCardProps,
  'badge' | 'delivery' | 'isNew' | 'product' | 'promoteAsSale' | 'promotion' | 'rating'
>;

interface AdaptProductToProductCardPropsOptions {
  currencyCode: CurrencyCode;
  /**
   * Whether every card in this listing comes from the same partner, as on a partner
   * page. Suppresses the badges that describe the partner rather than the product:
   * see `toProductCardBadge`.
   *
   * Required rather than defaulted, so that a new grid surface has to state which it is.
   */
  isSinglePartnerListing: boolean;
  /**
   * Passed in so that a list of products dates every delivery from the same instant,
   * and so that the date is deterministic in tests. Defaults for a single call.
   */
  now?: Date;
  /**
   * The authored promo labels, in precedence order. The card shows the first one listing
   * a campaign the product is in.
   *
   * Required rather than defaulted for the same reason as `isSinglePartnerListing`.
   */
  promoLabels: PromoLabel[];
  /**
   * Whether the site is presenting discounts as a sale rather than as an offer. Set
   * site-wide in Contentstack, so it is read from `useDiscountPresentationMode` at
   * the call site rather than derived from the product: no discount in the catalogue
   * distinguishes the two.
   *
   * Required rather than defaulted for the same reason as `isSinglePartnerListing`.
   */
  promoteAsSale: boolean;
}

const priceInCurrency = (prices: Price[] | null, currencyCode: CurrencyCode) =>
  prices?.find(({ currency }) => currency === currencyCode)?.amount;

/**
 * The card takes a structured date and cost and words the delivery line itself,
 * so every surface reads identically.
 *
 * A product with no quoted option gets no delivery line. Nothing is lost by that:
 * the card drops a line it cannot date anyway. An unpurchasable product is
 * skipped too, since the card shows "Out of stock" in that slot and ignores
 * `delivery` entirely.
 */
const adaptDelivery = (
  { delivery, deliveryByEvent, purchasable }: Product,
  now: Date,
): ProductCardProps['delivery'] => {
  if (!purchasable || delivery === null) {
    return undefined;
  }

  /* The store holds the raw event key (`christmas`) and the card renders it as
     "Est to arrive by {eventName}", so it needs the display name. Not
     `getDeliveryByEventLabel`, which prefixes it ("Arrives by Christmas") and
     would read as "Est to arrive by Arrives by Christmas". The lookup is typed as
     always returning a value but is a plain object index, so an unrecognised key
     falls through to no event rather than to `undefined.name`. */
  const eventName = deliveryByEvent?.arriveByEvent
    ? getDeliveryByEventName(deliveryByEvent.eventName)?.name
    : undefined;

  return {
    /* The store holds the delivery cost in major units, as the listings API gives
       it (3.99 for £3.99), where `ProductCardDelivery.cost` is in minor units like
       every other price the card takes. The rounding is load-bearing, not
       cosmetic: 3.99 * 100 is 399.00000000000006. */
    cost: Math.round(delivery.cost * 100),
    date: addDays(now, delivery.days),
    ...(eventName ? { eventName } : {}),
  };
};

/**
 * Maps a store product onto the design system `ProductCard`.
 *
 * Deliberately thin: whether a product is discounted, the discount percentage and
 * the out-of-stock treatment are all derived by the card from `price`,
 * `originalPrice` and `availability`, so `onSale` and `salePercentage` are not
 * passed through. `promoteAsSale` is the exception, since how a discount is
 * presented is a site-wide decision rather than a property of the product.
 */
export const adaptProductToProductCardProps = (
  product: Product,
  {
    currencyCode,
    isSinglePartnerListing,
    now = new Date(),
    promoLabels,
    promoteAsSale,
  }: AdaptProductToProductCardPropsOptions,
): ProductCardDataProps => {
  const {
    averageRating,
    code,
    detailPageHref,
    images,
    isNew,
    partner,
    preSalePrices,
    price,
    purchasable,
    title,
  } = product;

  const delivery = adaptDelivery(product, now);
  const originalPrice = priceInCurrency(preSalePrices, currencyCode);
  /* `rating` is not adapted here: the store has already applied the same "above zero
     means rated" gate in `adaptResponseProductsToStore`, which is where a semantic value
     belongs rather than in a card adapter. */
  const badge = toProductCardBadge(
    {
      bestseller: product.bestseller,
      furtherReduced: product.furtherReduced,
      // The shared rule calls Most Loved `highlyRated`; it is the Data team's merch flag.
      highlyRated: product.mostLoved,
      newToSale: product.newToSale,
      sellingFast: product.sellingFast,
      starBrand: product.starBrand,
    },
    { promoteAsSale, suppressPartnerBadges: isSinglePartnerListing },
  );
  const promoLabel = product.campaignIds ? matchPromoLabel(product.campaignIds, promoLabels) : null;

  return {
    ...(badge ? { badge } : {}),
    ...(delivery ? { delivery } : {}),
    isNew,
    ...(product.sponsored ? { isSponsored: true } : {}),
    product: {
      availability: purchasable ? 'in_stock' : 'out_of_stock',
      // The store keeps the numeric product code; the OOUX interface types it as a
      // string, since the basket API treats it as one.
      code: String(code),
      currencyCode,
      /* The array is handed over as it is. The card decides how many to show: it caps
         at five, renders a swipe carousel above one image and a plain frame at one,
         and shows a placeholder for none. */
      media: images.map((image, index) => ({
        /* Only the primary image is described. The card labels each slide "N of M"
           and wraps every one in a link named by the product, so repeating the title
           on each would announce the product several times over. */
        alt: index === 0 ? title : '',
        type: 'image',
        /* `ProductMedia.url` must be fully resolved. The store holds the URL with
           its `{size}` token still in place, and the card does not resolve it. */
        url: getProductImageURL(image),
      })),
      ...(originalPrice !== undefined ? { originalPrice } : {}),
      partner: { name: partner.name, shortcode: partner.shortcode },
      price: price.amount,
      title,
      url: detailPageHref,
    },
    promoteAsSale,
    ...(promoLabel ? { promotion: { label: promoLabel.cardLabel } } : {}),
    ...(averageRating !== undefined ? { rating: averageRating } : {}),
  };
};
